import React, { useCallback, useMemo, useState } from 'react';
import { DateRangePicker, getDefaultRange } from '../components/molecules/DateRangePicker';
import { UsersSection } from '../components/organisms/dashboard/UsersSection';
import { SubscriptionsSection } from '../components/organisms/dashboard/SubscriptionsSection';
import type { AnalyticsDateRange, DataSource } from '../types/api/dashboard';

/**
 * AnalyticsPage — Phase 1
 *
 * Composes the date-range picker, the Users section, and the Subscriptions
 * section. Each section owns its React Query hooks and reports its data
 * source upward via `onSourceChange` so the page header can render a single
 * Live / Mock badge that reflects the worst case across sections.
 *
 * Phase 2 will add Cohorts (Users) and Content sections.
 */
export const AnalyticsPage: React.FC = () => {
  const [range, setRange] = useState<AnalyticsDateRange>(() => getDefaultRange());
  const [usersSource, setUsersSource] = useState<DataSource>('live');
  const [subsSource, setSubsSource] = useState<DataSource>('live');

  const handleUsersSource = useCallback((s: DataSource) => setUsersSource(s), []);
  const handleSubsSource = useCallback((s: DataSource) => setSubsSource(s), []);

  const overallSource: DataSource = useMemo(
    () => (usersSource === 'mock' || subsSource === 'mock' ? 'mock' : 'live'),
    [usersSource, subsSource],
  );

  return (
    <div className="space-y-8">
      {/* Page header: title + date range + source badge */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="text-left">
          <h1 className="text-2xl font-bold text-gray-900">Analytics</h1>
          <p className="text-sm text-gray-600 mt-1">
            Users and subscriptions overview for the selected date range.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <DateRangePicker value={range} onChange={setRange} />
          {overallSource === 'live' ? (
            <div className="flex items-center gap-1.5 text-xs text-gray-500">
              <span className="relative flex h-2.5 w-2.5">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75" />
                <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-green-500" />
              </span>
              Live
            </div>
          ) : (
            <div className="flex items-center gap-1.5 rounded-full bg-amber-50 border border-amber-200 px-2.5 py-0.5 text-xs text-amber-700">
              <span className="inline-flex rounded-full h-2 w-2 bg-amber-400" />
              {usersSource === 'mock' && subsSource === 'mock'
                ? 'Mock data'
                : 'Partial mock'}
            </div>
          )}
        </div>
      </div>

      <UsersSection range={range} onSourceChange={handleUsersSource} />
      <SubscriptionsSection range={range} onSourceChange={handleSubsSource} />
    </div>
  );
};

export default AnalyticsPage;
